import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Input, Text, Field, Button, createListCollection } from '@chakra-ui/react'
import { colors } from '../../../utilities/colors'
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText } from '../../ui/select'
import US_STATES from '../../../utilities/statesList'
import useGooglePlaces from '../../../hooks/useGooglePlaces'
import AutocompleteDropdown from '../../common/AutocompleteDropdown'
import { emptyLocation, locationTypeOptions } from '../../../constants/carpoolLocation'

const stateOptions = createListCollection({
  items: US_STATES.map((state) => ({ label: `${state.id} - ${state.name}`, value: state.id })),
})

const locationTypes = createListCollection({ items: locationTypeOptions })

const isValidPostalCode = (code) => /^\d{5}(-\d{4})?$/.test(code || '')

const AddressFields = forwardRef(function AddressFields({ label, values, onChange, accentColor }, ref) {
  const { t } = useTranslation()
  const { isLoaded, getCityPredictions, resolvePlace, searchZip } = useGooglePlaces()
  const [errors, setErrors] = useState({})

  const hasValues = !!(values.city || values.state || values.postal_code || values.type)

  const handleClear = () => {
    onChange({ ...emptyLocation })
    setErrors({})
  }

  useImperativeHandle(ref, () => ({
    isValid: () => {
      const nextErrors = {}
      if (!isValidPostalCode(values.postal_code)) {
        nextErrors.postal_code = t('Enter a valid 5-digit zip code')
      }
      if (!values.state) {
        nextErrors.state = t('State is required')
      }
      if (!values.type) {
        nextErrors.type = t('Location type is required')
      }
      setErrors(nextErrors)
      return Object.keys(nextErrors).length === 0
    },
  }))

  const handleTypeChange = (val) => {
    onChange({ ...values, type: val.value[0] })
    clearError('type')
  }

  const clearError = (field) => {
    if (!errors[field]) return
    setErrors((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const handleCityChange = (event) => {
    const cityValue = event.target.value.replace(/[0-9]/g, '')
    onChange({ ...values, city: cityValue })
  }

  const handleCitySelect = async (prediction) => {
    const place = await resolvePlace(prediction.placeId)
    if (place) {
      onChange({
        city: place.city || values.city,
        state: place.state || values.state,
        postal_code: place.postal_code || values.postal_code,
      })
      if (place.state) clearError('state')
      if (place.postal_code) clearError('postal_code')
    }
  }

  const handleStateChange = (val) => {
    onChange({ ...values, state: val.value[0] })
    clearError('state')
  }

  const handleZipChange = async (event) => {
    const zipValue = event.target.value.replace(/[^\d-]/g, '')
    onChange({ ...values, postal_code: zipValue })
    clearError('postal_code')

    if (/^\d{5}$/.test(zipValue) && isLoaded && !values.city && !values.state) {
      const place = await searchZip(zipValue)
      if (place) {
        onChange({
          city: place.city || values.city,
          state: place.state || values.state,
          postal_code: zipValue,
        })
        if (place.state) clearError('state')
      }
    }
  }

  return (
    <Box
      w="full"
      p={4}
      borderRadius="md"
      border="1px solid"
      borderColor={colors.deepOcean}
      bg={colors.grey900}
      borderLeft="4px solid"
      borderLeftColor={accentColor}
    >
      <Text fontSize="md" fontWeight="semibold" color={accentColor} mb={1}>
        {label}
      </Text>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={3}>
        {hasValues && (
          <Button
            size="xs"
            variant="outline"
            color={colors.fpxRed}
            borderColor={colors.fpxRed}
            _hover={{ bg: colors.fpxRed, color: colors.grey100 }}
            onClick={handleClear}
          >
            {t('Clear')}
          </Button>
        )}
      </Box>
      <Field.Root mb={3} invalid={!!errors.type}>
        <Field.Label fontSize="sm" color={colors.grey300}>
          {t('Location Type')}
        </Field.Label>
        <SelectRoot
          collection={locationTypes}
          value={values.type ? [values.type] : []}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger
            borderWidth={1}
            borderColor={errors.type ? colors.fpxRed : colors.grey700}
            bgColor={colors.grey900}
            color={colors.grey100}
            _focus={{ borderColor: errors.type ? colors.fpxRed : colors.grey100 }}
            rounded="md"
            px={2}
          >
            <SelectValueText placeholder={t('Select Type')} />
          </SelectTrigger>
          <SelectContent portalled={false} width="full">
            {locationTypes.items.map((item) => (
              <SelectItem item={item} key={item.value} _hover={{ bgColor: colors.grey700, cursor: 'pointer' }}>
                {t(item.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
        {errors.type && (
          <Field.ErrorText fontSize="xs" color={colors.fpxRed}>
            {errors.type}
          </Field.ErrorText>
        )}
      </Field.Root>
      <Box display="flex" flexDirection={{ base: 'column', md: 'row' }} gap={3}>
        <Field.Root flex={2}>
          <Field.Label fontSize="sm" color={colors.grey300}>
            {t('City')}
          </Field.Label>
          <AutocompleteDropdown
            value={values.city}
            onChange={handleCityChange}
            onSelect={handleCitySelect}
            fetchOptions={getCityPredictions}
            getOptionKey={(option) => option.placeId}
            getOptionLabel={(option) => option.text?.text}
            inputProps={{
              bgColor: colors.grey900,
              borderColor: colors.grey700,
              color: colors.grey100,
              _focus: { borderColor: colors.grey100, ringColor: colors.grey100 },
            }}
          />
        </Field.Root>
        <Field.Root flex={1} invalid={!!errors.state}>
          <Field.Label fontSize="sm" color={colors.grey300}>
            {t('State')}
          </Field.Label>
          <SelectRoot
            collection={stateOptions}
            value={values.state ? [values.state] : []}
            onValueChange={handleStateChange}
          >
            <SelectTrigger
              borderWidth={1}
              borderColor={errors.state ? colors.fpxRed : colors.grey700}
              bgColor={colors.grey900}
              color={colors.grey100}
              _focus={{ borderColor: errors.state ? colors.fpxRed : colors.grey100 }}
              rounded="md"
              px={2}
            >
              <SelectValueText placeholder={t('Select State')} />
            </SelectTrigger>
            <SelectContent portalled={false} width="full">
              {stateOptions.items.map((item) => (
                <SelectItem item={item} key={item.value} _hover={{ bgColor: colors.grey700, cursor: 'pointer' }}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
          {errors.state && (
            <Field.ErrorText fontSize="xs" color={colors.fpxRed}>
              {errors.state}
            </Field.ErrorText>
          )}
        </Field.Root>
        <Field.Root flex={1} invalid={!!errors.postal_code}>
          <Field.Label fontSize="sm" color={colors.grey300}>
            {t('Zip')}
          </Field.Label>
          <Input
            value={values.postal_code}
            onChange={handleZipChange}
            maxLength={10}
            placeholder="e.g. 90210"
            autoComplete="off"
            bgColor={colors.grey900}
            borderColor={errors.postal_code ? colors.fpxRed : colors.grey700}
            color={colors.grey100}
            _focus={{
              borderColor: errors.postal_code ? colors.fpxRed : colors.grey100,
              ringColor: errors.postal_code ? colors.fpxRed : colors.grey100,
            }}
          />
          {errors.postal_code && (
            <Field.ErrorText fontSize="xs" color={colors.fpxRed}>
              {errors.postal_code}
            </Field.ErrorText>
          )}
        </Field.Root>
      </Box>
    </Box>
  )
})

export default AddressFields
